import React, { FC, useEffect, useRef, useState } from 'react';
import { FullScreenHandle } from 'react-full-screen';
import styled from 'styled-components';
import { Gantt as GanttAtom } from 'components/atoms/gantt';
import { PageLoading } from 'components/atoms/loading';
import { PermissionRuleName } from 'constant';
import { ProjectPlanFilter } from 'data/projects';
import { usePermissionForRule } from 'hooks/permissions/use-permission';
import { queueMacrotask } from 'utils/macrotask';
import { PrintSettings } from '../export-modal';
import { BaselineStyles } from './baseline';
import { useBaselineConfig } from './config/use-baseline-config';
import { useGanttDataConfig } from './config/use-data-config';
import { useGanttEventsConfig } from './config/use-events-config';
import { useGanttFilterConfig } from './config/use-filter-config';
import { useGanttStaticConfig } from './config/use-static-config';
import { ExportTimelineDialog } from './export';
import { TimelineToolbar } from './toolbar';
import { useGanttRef } from './use-gantt-ref';

// Ln 40 - 84
// TODO:: The following is a workaround until Bryntum supports React cells on TreeColumn type
const Gantt = styled(GanttAtom)`
  height: 100%;
  margin-bottom: var(--layout-01);

  .b-gridbase.b-split .b-grid-splitter {
    &.b-hover .b-grid-splitter-inner,
    &.b-moving .b-grid-splitter-inner {
      width: 5px;
      left: 0;
    }

    &.b-hover .b-icon {
      left: 0;
      position: absolute;
      transform: rotate(-90deg);

      &.b-icon-expand-gridregion {
        transform: rotate(90deg);
        left: -7px;
      }
    }
  }

  .b-grid-splitter-buttons {
    display: none;
  }

  .b-horizontaltimeaxis .b-sch-header-timeaxis-cell {
    border: none;
  }

  .b-grid-header {
    .b-sch-timerange.b-sch-current-time {
      background: var(--red-50);
      color: var(--text-color);
      font-weight: normal;
    }
  }

  .b-timeline-subgrid {
    .b-sch-current-time {
      border-left: solid 1px var(--red-50);
    }
  }

  &.b-hide-baselines .b-task-baseline {
    display: none;
  }
  .b-mask {
    visibility: hidden;
  }
`;

export const ProjectPlanTimelineView: FC<{
  filter: ProjectPlanFilter;
  projectId: number;
  projectName: string;
  handle: FullScreenHandle;
  showExportDialog: boolean;
  showPrivateTasks: boolean;
  onExportDialogCancel: VoidFunction;
  onExportSettingsChange: (settings: PrintSettings) => void;
  filters: any;
}> = ({
  filter,
  projectId,
  projectName,
  handle,
  showExportDialog,
  showPrivateTasks,
  onExportDialogCancel,
  onExportSettingsChange,
  filters,
}) => {
  const [showLoader, setShowLoader] = useState(true);
  const {
    ganttRef,
    setGanttRef,
    currentZoomLevel,
    collapseAll,
    expandAll,
    zoomIn,
    zoomOut,
    zoomToFit,
    shiftPrevious,
    shiftNext,
    onZoomTo,
  } = useGanttRef(showLoader);

  const staticConfig = useGanttStaticConfig({ ganttRef });
  const dataConfig = useGanttDataConfig({
    projectId,
  });
  const eventsConfig = useGanttEventsConfig({ projectId, ganttRef });

  useGanttFilterConfig({
    filter,
    showPrivateTasks,
    handle,
    ganttRef,
  });

  const {
    baselines,
    selection: selectedBaselines,
    setBaseline,
    toggleBaseline,
    config: baselineConfig,
  } = useBaselineConfig({
    ganttRef,
    projectId,
  });

  /**
   * `getPopupContainer` API of antd Dropdown component expects arg of type HTMLElement,
   * hence setting a dummy element as initial value, instead of null.
   */
  const popupContainer = useRef(document.createElement('div'));

  /**
   * Forcefully showing loader for zero timeout
   * for faster tab switching in project detail.
   * else, user stays on project plan until gantt loads.
   */
  useEffect(() => {
    queueMacrotask(() => {
      setShowLoader(false);
    });
  }, []);
  const { isAllowedForCurrentUser } = usePermissionForRule();

  return (
    <>
      <div ref={popupContainer}>
        <TimelineToolbar
          currentZoomLevel={currentZoomLevel}
          onExpandAll={expandAll}
          onCollapseAll={collapseAll}
          onZoomIn={zoomIn}
          onZoomOut={zoomOut}
          onZoomToFit={zoomToFit}
          onShiftPrevious={shiftPrevious}
          onShiftNext={shiftNext}
          onZoomTo={onZoomTo}
          popupContainer={popupContainer}
          projectId={projectId}
          selectedBaselines={selectedBaselines}
          setBaseline={setBaseline}
          toggleBaseline={toggleBaseline}
          ganttRef={ganttRef}
        />
      </div>
      {!showLoader && dataConfig.project.eventsData.length > 0 ? (
        <>
          {baselines && <BaselineStyles count={baselines.length} />}
          <Gantt
            cls="b-hide-baselines"
            {...staticConfig}
            {...dataConfig}
            {...eventsConfig}
            {...baselineConfig}
            canRemoveDependency={isAllowedForCurrentUser(
              PermissionRuleName.TaskRemoveDependentTaskAction,
            )}
            ganttRef={setGanttRef}
          />
        </>
      ) : (
        <PageLoading css />
      )}
      {showExportDialog ? (
        <ExportTimelineDialog
          projectName={projectName}
          filter={filter}
          filters={filters}
          ganttRef={ganttRef}
          onCancel={onExportDialogCancel}
          onSettingsChange={onExportSettingsChange}
        />
      ) : null}
    </>
  );
};
